import type { SalesDealQuestionnaireSummary, SalesDealTemplateFieldSummary } from '@protoss/shared';
import * as React from 'react';
import { GEO_COUNTRIES, GEO_STATES } from '../../../utils/geo-data';
import { salesApi } from '../services/salesApi';

/**
 * Sources that are resolved locally (no API call needed).
 * - geocoding:* - static bundled data or live Google Places API, handled in this hook
 * - field:options - suggestions come directly from the field's own `options` array (DealPage)
 */
const LOCALLY_RESOLVED_SOURCES = new Set([
  'geocoding:placeAutocomplete',
  'geocoding:stateOptions',
  'geocoding:countryOptions',
  'field:options',
]);

function getAnswerKey(field: SalesDealTemplateFieldSummary): string {
  return field.templateFieldId !== null ? String(field.templateFieldId) : field.fieldKey;
}

function getAllFields(
  questionnaire: SalesDealQuestionnaireSummary
): SalesDealTemplateFieldSummary[] {
  return [
    ...(questionnaire.headerFields ?? []),
    ...questionnaire.sections.flatMap((s) => s.groups).flatMap((g) => g.fields),
  ];
}

/**
 * Manages autocomplete suggestion maps for a deal questionnaire.
 *
 * - Pre-fetches static workspace sources (e.g. company:industry) once on mount.
 * - Pre-populates static geocoding lists (countries, states) from bundled data.
 * - Debounces live Google Places lookups when the user types into a
 *   `geocoding:placeAutocomplete` field.
 * - When the user selects a Places suggestion, fetches structured address components
 *   and auto-fills sibling fields declared via `autocompletePopulates` in the template.
 */
export function useQuestionnaireAutocomplete(
  questionnaire: SalesDealQuestionnaireSummary | null | undefined,
  onAnswerChange: (fieldKey: string, value: unknown) => void
): {
  autocompleteValues: Record<string, string[]>;
  onAnswerChange: (fieldKey: string, value: unknown) => void;
  /** Call when user selects a Places suggestion - triggers address decomposition */
  onPlaceSelect: (fieldKey: string, label: string) => void;
  /** Call when user selects from a customer:email autocomplete - triggers field auto-population */
  onCustomerSelect: (fieldKey: string, email: string) => void;
  /** True while a Google Places autocomplete API call is in-flight */
  placesLoading: boolean;
} {
  const [autocompleteValues, setAutocompleteValues] = React.useState<Record<string, string[]>>({});
  const [placesLoading, setPlacesLoading] = React.useState(false);

  // answerKey → { label → placeId } - populated when autocomplete results arrive
  const placeIdMapRef = React.useRef<Record<string, Record<string, string>>>({});

  // Answer keys for geocoding:placeAutocomplete fields
  const geoPlaceAnswerKeys = React.useMemo(() => {
    if (!questionnaire) return new Set<string>();
    return new Set(
      getAllFields(questionnaire)
        .filter((f) => f.autocompleteSource === 'geocoding:placeAutocomplete')
        .map(getAnswerKey)
    );
  }, [questionnaire]);

  // Answer keys for customer:email autocomplete fields
  const customerEmailAnswerKeys = React.useMemo(() => {
    if (!questionnaire) return new Set<string>();
    return new Set(
      getAllFields(questionnaire)
        .filter((f) => f.autocompleteSource === 'customer:email')
        .map(getAnswerKey)
    );
  }, [questionnaire]);

  // fieldKey → answerKey (needed to call onAnswerChange for sibling fields)
  const fieldKeyToAnswerKey = React.useMemo(() => {
    if (!questionnaire) return new Map<string, string>();
    return new Map(getAllFields(questionnaire).map((f) => [f.fieldKey, getAnswerKey(f)]));
  }, [questionnaire]);

  // answerKey → autocompletePopulates { componentName → sibling fieldKey }
  const autocompletePopulatesMap = React.useMemo(() => {
    if (!questionnaire) return new Map<string, Record<string, string>>();
    const result = new Map<string, Record<string, string>>();
    for (const f of getAllFields(questionnaire)) {
      const populates = f.displayHints?.autocompletePopulates;
      if (populates && Object.keys(populates).length > 0) {
        result.set(getAnswerKey(f), populates);
      }
    }
    return result;
  }, [questionnaire]);

  const addressDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const wrappedOnAnswerChange = React.useCallback(
    (fieldKey: string, value: unknown) => {
      onAnswerChange(fieldKey, value);

      if (!geoPlaceAnswerKeys.has(fieldKey)) return;
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
      const q = typeof value === 'string' ? value.trim() : '';
      if (!q) {
        setAutocompleteValues((prev) => ({ ...prev, 'geocoding:placeAutocomplete': [] }));
        placeIdMapRef.current[fieldKey] = {};
        setPlacesLoading(false);
        return;
      }
      addressDebounceRef.current = setTimeout(() => {
        setPlacesLoading(true);
        salesApi
          .addressAutocomplete(q)
          .then(({ values, placeIds }) => {
            setAutocompleteValues((prev) => ({
              ...prev,
              'geocoding:placeAutocomplete': values,
            }));
            placeIdMapRef.current[fieldKey] = placeIds;
          })
          .catch(() => {
            /* non-fatal */
          })
          .finally(() => {
            setPlacesLoading(false);
          });
      }, 300);
    },
    [onAnswerChange, geoPlaceAnswerKeys]
  );

  const onPlaceSelect = React.useCallback(
    (fieldKey: string, label: string) => {
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
      const placeId = placeIdMapRef.current[fieldKey]?.[label];
      const populates = autocompletePopulatesMap.get(fieldKey) ?? {};

      // 1. Immediately set the field to the full label - instant feedback
      onAnswerChange(fieldKey, label);

      // 2. Clear suggestions so the dropdown closes
      setAutocompleteValues((prev) => ({ ...prev, 'geocoding:placeAutocomplete': [] }));
      placeIdMapRef.current[fieldKey] = {};
      setPlacesLoading(false);

      if (!placeId) return;

      // 3. Fetch structured address components and replace with decomposed values
      salesApi
        .addressDetails(placeId)
        .then((components) => {
          if (components.street) onAnswerChange(fieldKey, components.street);
          const componentByName: Record<string, string | null> = {
            city: components.city,
            state: components.state,
            postalCode: components.postalCode,
            country: components.country,
          };
          for (const [componentName, siblingFieldKey] of Object.entries(populates)) {
            const siblingAnswerKey = fieldKeyToAnswerKey.get(siblingFieldKey);
            if (siblingAnswerKey) {
              onAnswerChange(siblingAnswerKey, componentByName[componentName] ?? '');
            }
          }
        })
        .catch(() => {
          /* keep full label on failure */
        });
    },
    [onAnswerChange, autocompletePopulatesMap, fieldKeyToAnswerKey]
  );

  /**
   * Called when the user selects an email from a customer:email autocomplete.
   * Fetches the matching customer's canonical fields and populates sibling form fields
   * as declared in the field's `autocompletePopulates` display hint.
   */
  const onCustomerSelect = React.useCallback(
    (fieldKey: string, email: string) => {
      if (!customerEmailAnswerKeys.has(fieldKey)) return;
      const populates = autocompletePopulatesMap.get(fieldKey) ?? {};
      if (Object.keys(populates).length === 0) return;

      salesApi
        .customerByEmail(email)
        .then(({ customer }) => {
          if (!customer) return;
          const componentByName: Record<string, string | null> = {
            name: customer.name,
            title: customer.title,
            department: customer.department,
            emailAlt: customer.emailAlt,
            phone: customer.phone,
            phoneAlt: customer.phoneAlt,
          };
          for (const [componentName, siblingFieldKey] of Object.entries(populates)) {
            const siblingAnswerKey = fieldKeyToAnswerKey.get(siblingFieldKey);
            if (siblingAnswerKey && componentByName[componentName] != null) {
              onAnswerChange(siblingAnswerKey, componentByName[componentName]);
            }
          }
        })
        .catch(() => {
          /* non-fatal - user can fill fields manually */
        });
    },
    [onAnswerChange, autocompletePopulatesMap, fieldKeyToAnswerKey, customerEmailAnswerKeys]
  );

  React.useEffect(() => {
    if (!questionnaire) return;
    const allFields = getAllFields(questionnaire);

    // Pre-populate static geocoding sources immediately (no API call needed).
    setAutocompleteValues((prev) => ({
      ...prev,
      'geocoding:countryOptions': GEO_COUNTRIES,
      'geocoding:stateOptions': GEO_STATES,
      'geocoding:placeAutocomplete': prev['geocoding:placeAutocomplete'] ?? [],
    }));

    const sources = allFields
      .flatMap((f) => {
        if (f.uiControl === 'table') {
          // Collect autocomplete sources declared on individual table columns
          return (f.displayHints?.columns ?? [])
            .map((c) => c.autocompleteSource)
            .filter((s): s is string => Boolean(s));
        }
        if (f.uiControl === 'autocomplete' && f.autocompleteSource) {
          return [f.autocompleteSource];
        }
        return [];
      })
      .filter((s) => !LOCALLY_RESOLVED_SOURCES.has(s))
      .filter((v, i, arr) => arr.indexOf(v) === i);
    if (sources.length === 0) return;
    let cancelled = false;
    Promise.all(
      sources.map(async (source) => {
        const { values } = await salesApi.getFieldValuesBySource(source);
        return [source, values] as [string, string[]];
      })
    )
      .then((entries) => {
        if (!cancelled)
          setAutocompleteValues((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      })
      .catch(() => {
        // Non-fatal - autocomplete will show no suggestions
      });
    return () => {
      cancelled = true;
    };
  }, [questionnaire]);

  return {
    autocompleteValues,
    onAnswerChange: wrappedOnAnswerChange,
    onPlaceSelect,
    onCustomerSelect,
    placesLoading,
  };
}
